// ErrorBoundary — catches render-time crashes anywhere below in the
// tree and shows the actual error + component stack.  Without this
// a React error bubbles up as "Uncaught TypeError" in the console
// with a minified stack trace — impossible to diagnose without
// source maps.
//
// Mounted once at the root of main.jsx around <Nuggets />.

import React from 'react';

const NAVY  = '#0D1B2A';
const ORANGE = '#E87722';
const TEXT   = '#F0F4F8';
const MUTED  = 'rgba(255,255,255,0.55)';
const BORDER = 'rgba(255,255,255,0.10)';
const RED    = '#EF4444';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Also log to the console so it's visible in DevTools + captured
    // by any monitoring hook.  The `info.componentStack` string is the
    // React component-tree path — that's the piece minified stacks
    // don't give you.
    console.error('[error-boundary]', error);
    console.error('[error-boundary] component stack:', info?.componentStack);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{
        position: 'fixed', inset: 0, background: NAVY, color: TEXT,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: "'Montserrat',sans-serif",
        overflow: 'auto',
      }}>
        <div style={{
          maxWidth: 720, width: '100%',
          border: `1px solid ${BORDER}`, borderRadius: 10,
          padding: 22, background: 'rgba(255,255,255,0.02)',
        }}>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', color: RED, fontWeight: 800, textTransform: 'uppercase', marginBottom: 6 }}>
            Something broke
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.01em', marginBottom: 14 }}>
            {String(this.state.error?.message || 'Unknown error')}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 14, lineHeight: 1.55 }}>
            The app hit a render error and stopped.  This is a bug — please copy the
            details below and send them to the dev team.
          </div>

          {this.state.info?.componentStack && (
            <details open style={{ marginBottom: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: ORANGE, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
                Component stack
              </summary>
              <pre style={{
                fontSize: 11, lineHeight: 1.45,
                background: 'rgba(0,0,0,0.35)', color: '#C8D3E0',
                padding: 12, borderRadius: 6, overflow: 'auto',
                margin: 0, maxHeight: 240,
                fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
              }}>{this.state.info.componentStack}</pre>
            </details>
          )}

          {this.state.error?.stack && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: ORANGE, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
                JS stack
              </summary>
              <pre style={{
                fontSize: 11, lineHeight: 1.45,
                background: 'rgba(0,0,0,0.35)', color: '#C8D3E0',
                padding: 12, borderRadius: 6, overflow: 'auto',
                margin: 0, maxHeight: 240,
                fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
              }}>{this.state.error.stack}</pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              onClick={this.reload}
              style={{
                background: ORANGE, color: '#fff', border: 'none',
                padding: '10px 20px', borderRadius: 6,
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Reload the app
            </button>
            <button
              onClick={this.reset}
              style={{
                background: 'transparent', color: MUTED,
                border: `1px solid ${BORDER}`,
                padding: '10px 16px', borderRadius: 6,
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Try to continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
